import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CarteTerritoire, centreEmprise, emprise, type MarqueurCarte } from '@eec/ui';
import { lireLimites } from './CarteEquipements';

export function CarteMessagesPublics({
  messages,
}: {
  messages: { province_code: string; province: string }[];
}) {
  const [zoom, setZoom] = useState('gabon');
  const limites = useQuery({
    queryKey: ['limites-cartographiques-gabon'],
    queryFn: ({ signal }) => lireLimites(signal),
    staleTime: Infinity,
  });
  const groupes = new Map<string, { nom: string; nombre: number }>();
  for (const m of messages) {
    const g = groupes.get(m.province_code) ?? { nom: m.province, nombre: 0 };
    g.nombre++;
    groupes.set(m.province_code, g);
  }
  const echelles = limites.data?.filter((t) => t.id === 'gabon' || groupes.has(t.id)) ?? [];
  const courante = echelles.some((t) => t.id === zoom) ? zoom : 'gabon';
  const marqueurs: MarqueurCarte[] = [...groupes].flatMap(([id, g], i) => {
    const t = echelles.find((t) => t.id === id);
    if (!t?.geometrie) return [];
    const [longitude, latitude] = centreEmprise(emprise(t.geometrie));
    return [
      {
        id,
        numero: i + 1,
        longitude,
        latitude,
        libelle: `${g.nom} : ${g.nombre} message(s) public(s)`,
        ton: 'autre',
      },
    ];
  });
  return (
    <section aria-label="Carte des messages publics" className="eec-carte">
      <h3>Messages publiés par province</h3>
      <p>
        Les repères représentent les provinces des annonces ci-dessous, pas les lieux de panne. Une
        province sans annonce ne signifie pas l’absence d’incident. Les limites sont indicatives.
      </p>
      {limites.error && (
        <p role="alert">Carte indisponible ; les messages restent consultables ci-dessous.</p>
      )}
      {limites.isPending && <p>Chargement de la carte…</p>}
      {echelles.length > 0 && (
        <CarteTerritoire
          titre="Messages publics au Gabon"
          echelles={echelles}
          echelle={courante}
          onEchelle={setZoom}
          marqueurs={marqueurs}
          onMarqueur={setZoom}
          marqueurActif={courante === 'gabon' ? null : courante}
          fond={null}
          hauteur={360}
          surfaces={{
            type: 'FeatureCollection',
            features: echelles
              .filter(
                (t) =>
                  t.niveau === 'province' &&
                  t.geometrie &&
                  (courante === 'gabon' || t.id === courante),
              )
              .map((t) => ({
                type: 'Feature',
                properties: { libelle: t.libelle },
                geometry: t.geometrie!,
              })),
          }}
          libellesTon={{
            autre: 'Avec message public',
            ouvert: 'Avec message public',
            retard: 'Avec message public',
          }}
          libelleObjets={{ singulier: 'province avec annonce', pluriel: 'provinces avec annonce' }}
        />
      )}
      {limites.data && marqueurs.length !== groupes.size && (
        <p role="alert">
          Certaines annonces ne peuvent pas être placées sur la carte. Consultez leur province dans
          la liste.
        </p>
      )}
      <ul aria-label="Nombre de messages par province">
        {[...groupes].map(([id, g]) => (
          <li key={id}>
            {g.nom} : {g.nombre} message(s) public(s)
          </li>
        ))}
      </ul>
    </section>
  );
}
